import { useMemo } from 'react';
import paper from 'paper';

function extractAllPoints(item) {
  let points = [];
  if (!item) return points;

  let targetItem = item;
  let createdTempPath = false;

  if (item instanceof paper.Shape) {
    targetItem = item.toPath(false);
    createdTempPath = true;
  }

  if (targetItem.children && targetItem.children.length > 0) {
    targetItem.children.forEach((child) => {
      points = points.concat(extractAllPoints(child));
    });
  } else if (targetItem.segments && targetItem.segments.length > 0) {
    const flatClone = targetItem.clone({ insert: false });
    flatClone.flatten(0.1);
    flatClone.segments.forEach((s) => {
      points.push(s.point);
    });
    flatClone.remove();
  }

  if (createdTempPath) {
    targetItem.remove();
  }

  return points;
}

export function calculateMMBounds(item, dpi = 96) {
  if (!item) return null;

  const scaleFactor = 25.4 / dpi;
  const bounds = item.geometricBounds || item.bounds;
  if (!bounds || (bounds.width === 0 && bounds.height === 0)) return null;

  const widthMm = bounds.width * scaleFactor;
  const heightMm = bounds.height * scaleFactor;

  const centerPx = bounds.center;
  const points = extractAllPoints(item);

  let maxRadiusPx = 0;
  if (points.length > 0) {
    points.forEach((p) => {
      const dx = p.x - centerPx.x;
      const dy = p.y - centerPx.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > maxRadiusPx) {
        maxRadiusPx = dist;
      }
    });
  }

  if (maxRadiusPx === 0) {
    maxRadiusPx = Math.sqrt(bounds.width ** 2 + bounds.height ** 2) / 2;
  }

  const maxDiameterPx = maxRadiusPx * 2;
  const maxDiameterMm = maxDiameterPx * scaleFactor;

  return {
    widthPx: bounds.width,
    heightPx: bounds.height,
    widthMm,
    heightMm,
    maxDiameterMm,
    geoCenter: { x: centerPx.x, y: centerPx.y },
  };
}

export function useItemBounds(selectedItems = [], dpi = 96) {
  return useMemo(() => {
    if (!selectedItems || selectedItems.length === 0) return null;

    if (selectedItems.length === 1) {
      return calculateMMBounds(selectedItems[0], dpi);
    }

    const scaleFactor = 25.4 / dpi;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    selectedItems.forEach((item) => {
      const b = item.geometricBounds || item.bounds;
      if (b) {
        if (b.x < minX) minX = b.x;
        if (b.y < minY) minY = b.y;
        if (b.x + b.width > maxX) maxX = b.x + b.width;
        if (b.y + b.height > maxY) maxY = b.y + b.height;
      }
    });

    if (minX === Infinity) return null;

    const wPx = maxX - minX;
    const hPx = maxY - minY;
    const compoundDiagonalPx = Math.sqrt(wPx * wPx + hPx * hPx);

    return {
      widthMm: wPx * scaleFactor,
      heightMm: hPx * scaleFactor,
      maxDiameterMm: compoundDiagonalPx * scaleFactor,
    };
  }, [selectedItems, dpi]);
}